import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { createPortal } from 'react-dom';
import CachedProfileImage from './CachedProfileImage';
import { ClassMember } from '../types/alarm';
import {
  MemberProfileDisplayMode,
  readMemberProfileDisplayMode,
  resolveMemberProfileSource
} from '../utils/memberProfileDisplay';
import {
  getDefaultFloatingModalLayout,
  getLayoutViewportSize,
  PAGE_ZOOM_CHANGED_EVENT,
} from '../utils/pageZoom';

interface DiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  isHidden?: boolean;
  onRequestHide?: () => void;
  members: ClassMember[];
  userId?: string;
  classId?: string;
}

type DiceTab = 'number' | 'profile' | 'seat' | 'team';

const toNumber = (v: unknown): number => {
  const n = parseInt(String(v ?? ''), 10);
  return Number.isFinite(n) ? n : NaN;
};

const DICE_SEAT_ROTATE_MAX = 15;
const DICE_MODAL_LAST_TAB_KEY = 'diceModal:lastTab';

/** 멤버들의 seatPositions에서 표시할 월 키 (현재 월 우선, 없으면 최신 월) */
function resolveSeatMonthKey(members: ClassMember[]): string {
  const keys = new Set<string>();
  members.forEach((m) => {
    if (m.seatPositions && typeof m.seatPositions === 'object') {
      Object.keys(m.seatPositions).forEach((k) => keys.add(k));
    }
  });
  if (keys.size === 0) return '';
  const now = new Date();
  const current = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  if (keys.has(current)) return current;
  return Array.from(keys).sort().reverse()[0];
}

type DiceSeatAssigned = { member: ClassMember; seatRow: number; seatCol: number };

function buildDiceSeatLayout(items: DiceSeatAssigned[], isRotated: boolean, seatScale: number) {
  const assigned = items.map(({ member, seatRow, seatCol }) => ({
    member,
    seatRow: isRotated ? DICE_SEAT_ROTATE_MAX - seatRow : seatRow,
    seatCol: isRotated ? DICE_SEAT_ROTATE_MAX - seatCol : seatCol,
  }));

  let minRow = 0;
  let maxRow = DICE_SEAT_ROTATE_MAX;
  let minCol = 0;
  let maxCol = DICE_SEAT_ROTATE_MAX;
  if (assigned.length > 0) {
    const rows = assigned.map((a) => a.seatRow);
    const cols = assigned.map((a) => a.seatCol);
    minRow = Math.max(0, Math.min(...rows));
    maxRow = Math.min(DICE_SEAT_ROTATE_MAX, Math.max(...rows));
    minCol = Math.max(0, Math.min(...cols));
    maxCol = Math.min(DICE_SEAT_ROTATE_MAX, Math.max(...cols));
  }

  const rowCount = Math.max(2, maxRow - minRow + 2);
  const colCount = Math.max(2, maxCol - minCol + 2);
  const usedGridRows = new Set<number>();
  const usedGridCols = new Set<number>();
  assigned.forEach(({ seatRow, seatCol }) => {
    const rowIndex = seatRow - minRow;
    const colIndex = seatCol - minCol;
    [rowIndex, rowIndex + 1].forEach((idx) => {
      if (idx >= 0 && idx < rowCount) usedGridRows.add(idx);
    });
    [colIndex, colIndex + 1].forEach((idx) => {
      if (idx >= 0 && idx < colCount) usedGridCols.add(idx);
    });
  });

  // 모달 크기에 따라 그리드 셀의 px 값을 비율로 스케일링
  const usedRowPx = 43 * seatScale;
  const emptyRowPx = 21 * seatScale;
  const usedColPx = 92 * seatScale;
  const emptyColPx = 46 * seatScale;

  const usedColsCount = usedGridCols.size;
  const rowTemplate = Array.from({ length: rowCount }, (_, idx) => (
    usedGridRows.has(idx) ? `${Math.round(usedRowPx)}px` : `${Math.round(emptyRowPx)}px`
  )).join(' ');
  const colTemplate = Array.from({ length: colCount }, (_, idx) => (
    usedGridCols.has(idx) ? `${Math.round(usedColPx)}px` : `${Math.round(emptyColPx)}px`
  )).join(' ');

  // 메타 정보(너비 계산용)도 함께 반환
  return { minRow, minCol, rowTemplate, colTemplate, assigned, colCount, usedColsCount };
}

/* 개인공지 BoardPage RotateButton* 와 동일 — DiceModal보다 먼저 정의 (순환/HMR 시 ReferenceError 방지) */
const DiceSeatRotateButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 16px;
  margin-bottom: 8px;
`;

const DiceSeatRotateButton = styled.button.attrs({ type: 'button', className: 'touch-compact' })`
  width: 60px;
  height: 26px;
  min-height: 26px;
  max-height: 26px;
  min-width: 60px;
  box-sizing: border-box;
  line-height: 1;
  background: var(--color-grid-alignment-container);
  border: 1px solid var(--color-border);
  border-radius: 6px;
  color: var(--color-text-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 2px;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;

  :root[data-theme='light'] & {
    border: 1px solid rgba(255, 255, 255, 0.45);
    background: transparent;
    color: white;
    border-radius: 6px;
  }

  :root[data-theme='light'] &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.5);
  }

  :root[data-theme='light'] &:active {
    transform: none;
  }

  :root:not([data-theme='light']) & {
    &:hover {
      border-color: var(--color-border);
      background: var(--color-grid-alignment-container);
    }

    &:active {
      transform: none;
    }
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const DiceSeatRotateIconWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  background: var(--color-grid-alignment-active-bg);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;

  :root[data-theme='light'] & {
    background: rgba(255, 255, 255, 0.2);
  }
`;

const DiceSeatRotateArrowIcon = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 1;
  transform: scale(1);
  transition:
    opacity 0.3s ease,
    transform 0.3s ease;

  svg {
    width: 22px;
    height: 22px;
  }

  ${DiceSeatRotateButton}:hover & {
    opacity: 0;
    transform: scale(0.8);
  }
`;

const DiceSeatRotateNumberIcon = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  color: currentColor;
  opacity: 0;
  transform: scale(0.8);
  transition:
    opacity 0.3s ease,
    transform 0.3s ease;

  ${DiceSeatRotateButton}:hover & {
    opacity: 1;
    transform: scale(1);
  }
`;

const DiceModal: React.FC<DiceModalProps> = ({
  isOpen,
  onClose,
  isHidden = false,
  onRequestHide,
  members,
  userId,
  classId
}) => {
  const [memberProfileDisplayMode, setMemberProfileDisplayMode] = useState<MemberProfileDisplayMode>('svg');

  useEffect(() => {
    if (!userId || !classId) return;
    setMemberProfileDisplayMode(readMemberProfileDisplayMode(userId, classId));
  }, [userId, classId]);

  useEffect(() => {
    if (!userId || !classId) return;
    const handler = () => {
      setMemberProfileDisplayMode(readMemberProfileDisplayMode(userId, classId));
    };
    window.addEventListener('memberProfileDisplayModeChanged', handler as EventListener);
    return () => {
      window.removeEventListener('memberProfileDisplayModeChanged', handler as EventListener);
    };
  }, [userId, classId]);
  const memberNumbers = useMemo(
    () => members.map((m) => toNumber(m.number)).filter((n) => !Number.isNaN(n)).sort((a, b) => a - b),
    [members]
  );
  const defaultStart = memberNumbers[0] ?? 1;
  const defaultEnd = memberNumbers[memberNumbers.length - 1] ?? 30;

  const [selectedTab, setSelectedTab] = useState<DiceTab>(() => {
    if (typeof window === 'undefined') return 'number';
    try {
      const saved = window.localStorage.getItem(DICE_MODAL_LAST_TAB_KEY);
      if (saved === 'number' || saved === 'profile' || saved === 'seat' || saved === 'team') return saved;
    } catch {
      // ignore localStorage access error
    }
    return 'number';
  });
  const [startNumber, setStartNumber] = useState(defaultStart);
  const [endNumber, setEndNumber] = useState(defaultEnd);
  const [continuousCount, setContinuousCount] = useState(1);
  const [teamCount, setTeamCount] = useState(2);
  const [showTeamCountModal, setShowTeamCountModal] = useState(false);

  const [numberDrawnNumbers, setNumberDrawnNumbers] = useState<Set<number>>(new Set());
  const [numberResult, setNumberResult] = useState<number | null>(null);
  const [numberResults, setNumberResults] = useState<number[]>([]);

  const [profileDrawnNumbers, setProfileDrawnNumbers] = useState<Set<number>>(new Set());
  const [profileResult, setProfileResult] = useState<ClassMember | null>(null);
  const [profileResults, setProfileResults] = useState<ClassMember[]>([]);

  const [seatDrawnNumbers, setSeatDrawnNumbers] = useState<Set<number>>(new Set());
  const [seatResult, setSeatResult] = useState<ClassMember | null>(null);
  const [seatResults, setSeatResults] = useState<ClassMember[]>([]);
  const [isSeatRotated, setIsSeatRotated] = useState(false);

  const [teamResult, setTeamResult] = useState<ClassMember[][] | null>(null);

  const [isMinimized, setIsMinimized] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const [position, setPosition] = useState({ x: 120, y: 72 });
  const [size, setSize] = useState({ width: 980, height: 760 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isResizing, setIsResizing] = useState(false);
  const [resizeDirection, setResizeDirection] = useState<'nw' | 'ne' | 'sw' | 'se' | null>(null);
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, width: 0, height: 0, left: 0, top: 0 });
  const [windowSize, setWindowSize] = useState(() => {
    if (typeof window === 'undefined') return { w: 1920, h: 1080 };
    const { width, height } = getLayoutViewportSize();
    return { w: width, h: height };
  });

  const maxTeamCount = useMemo(() => {
    const matching = members.filter((m) => {
      const n = toNumber(m.number);
      return !Number.isNaN(n) && n >= startNumber && n <= endNumber;
    });
    return Math.max(2, Math.floor(matching.length / 2));
  }, [members, startNumber, endNumber]);

  const profileAvailableMembers = useMemo(
    () =>
      members.filter((m) => {
        const n = toNumber(m.number);
        return !Number.isNaN(n) && n >= startNumber && n <= endNumber;
      }),
    [members, startNumber, endNumber]
  );

  const availableNumbers = useMemo(() => {
    const arr: number[] = [];
    for (let i = startNumber; i <= endNumber; i += 1) {
      if (!numberDrawnNumbers.has(i)) arr.push(i);
    }
    return arr;
  }, [startNumber, endNumber, numberDrawnNumbers]);

  const availableProfiles = useMemo(
    () =>
      profileAvailableMembers.filter((m) => {
        const n = toNumber(m.number);
        return !Number.isNaN(n) && !profileDrawnNumbers.has(n);
      }),
    [profileAvailableMembers, profileDrawnNumbers]
  );

  const numberRemainingCount = availableNumbers.length;
  const profileRemainingCount = availableProfiles.length;

  const seatMonthKey = useMemo(() => resolveSeatMonthKey(members), [members]);

  const seatAssignedItems = useMemo((): DiceSeatAssigned[] => {
    if (!seatMonthKey) return [];
    return members
      .map((m) => {
        const n = toNumber(m.number);
        if (Number.isNaN(n) || n < startNumber || n > endNumber) return null;
        const pos = m.seatPositions?.[seatMonthKey];
        if (!pos || typeof pos.row !== 'number' || typeof pos.col !== 'number') return null;
        return { member: m, seatRow: pos.row, seatCol: pos.col };
      })
      .filter((x): x is DiceSeatAssigned => x !== null);
  }, [members, seatMonthKey, startNumber, endNumber]);

  // 자리배치 뽑기: 모달 가로에 맞춰 스케일(1배 미만 허용). 넓은 모달이면 상한 2.25배까지 확대.
  const seatLayoutMetaAt1 = useMemo(
    () => buildDiceSeatLayout(seatAssignedItems, isSeatRotated, 1),
    [seatAssignedItems, isSeatRotated]
  );

  const modalWidthForSeat = isMaximized ? windowSize.w : size.width;
  const modalHeightForSeat = isMaximized ? windowSize.h : size.height;

  const seatScaleWanted = Math.min(
    2.25,
    Math.min(modalWidthForSeat / 300, modalHeightForSeat / 350)
  );

  // 스케일 1배일 때 그리드 콘텐츠 가로합(열 너비 + gap + DiceSeatGrid 좌우 padding 4+4)
  const seatColGapPx = 2;
  const gridWidthAt1 =
    seatLayoutMetaAt1.usedColsCount * 92 +
    (seatLayoutMetaAt1.colCount - seatLayoutMetaAt1.usedColsCount) * 46 +
    Math.max(0, seatLayoutMetaAt1.colCount - 1) * seatColGapPx +
    8;

  const availableWidthForGrid = Math.max(8, modalWidthForSeat - 96);

  const maxSeatScaleForWidth = gridWidthAt1 > 0 ? availableWidthForGrid / gridWidthAt1 : seatScaleWanted;

  // 레이아웃이 0에 가까워지지 않도록 아주 작은 하한만 둠(표시 가능한 최소 크기)
  const seatScale = Math.max(0.08, Math.min(seatScaleWanted, maxSeatScaleForWidth));

  /** 가로 800px 이하: 자리 탭에서 프로필 숨기고 이름만 블럭 가운데 */
  const seatNarrowLayout = modalWidthForSeat <= 800;

  const diceSeatLayout = useMemo(
    () => buildDiceSeatLayout(seatAssignedItems, isSeatRotated, seatScale),
    [seatAssignedItems, isSeatRotated, seatScale]
  );

  const availableSeatMembers = useMemo(
    () =>
      seatAssignedItems
        .map((x) => x.member)
        .filter((m) => {
          const n = toNumber(m.number);
          return !Number.isNaN(n) && !seatDrawnNumbers.has(n);
        }),
    [seatAssignedItems, seatDrawnNumbers]
  );

  const seatRemainingCount = availableSeatMembers.length;

  const currentSeatHighlightIds = useMemo(() => {
    if (seatResults.length > 0) return new Set(seatResults.map((m) => m.id));
    if (seatResult) return new Set([seatResult.id]);
    return new Set<string>();
  }, [seatResult, seatResults]);

  const hasAnySeatDraw = seatDrawnNumbers.size > 0;

  const pad2 = (n: number) => String(n).padStart(2, '0');

  useEffect(() => {
    if (!isOpen) return;
    // 모달을 열 때마다 현재 그룹 멤버 기준으로 시작/끝 번호를 다시 초기화
    setStartNumber(defaultStart);
    setEndNumber(defaultEnd);
    setContinuousCount(1);
    setTeamCount(2);
  }, [isOpen, defaultStart, defaultEnd]);

  useEffect(() => {
    try {
      window.localStorage.setItem(DICE_MODAL_LAST_TAB_KEY, selectedTab);
    } catch {
      // ignore localStorage access error
    }
  }, [selectedTab]);

  // 모달이 열릴 때마다 화면 80% 크기 + 정중앙 배치로 자동 세팅 (CSS zoom 보정)
  useEffect(() => {
    if (!isOpen) return;
    if (typeof window === 'undefined') return;

    // 닫기/재열 시 위치/크기 기억 필요 없으므로 항상 초기 배치
    setIsMinimized(false);
    setIsMaximized(false);
    setIsDragging(false);
    setIsResizing(false);
    setResizeDirection(null);

    const { size: nextSize, position: nextPosition, viewport } = getDefaultFloatingModalLayout();
    setSize(nextSize);
    setPosition(nextPosition);
    setWindowSize({ w: viewport.w, h: viewport.h });
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !isMaximized) return;
    const syncViewport = () => {
      const { width, height } = getLayoutViewportSize();
      setWindowSize({ w: width, h: height });
    };
    syncViewport();
    window.addEventListener('resize', syncViewport);
    window.addEventListener(PAGE_ZOOM_CHANGED_EVENT, syncViewport);
    return () => {
      window.removeEventListener('resize', syncViewport);
      window.removeEventListener(PAGE_ZOOM_CHANGED_EVENT, syncViewport);
    };
  }, [isOpen, isMaximized]);

  useEffect(() => {
    if (!isOpen || isMaximized || isMinimized) return;
    const onPageZoomChange = () => {
      const { size: nextSize, position: nextPosition, viewport } = getDefaultFloatingModalLayout();
      setSize(nextSize);
      setPosition(nextPosition);
      setWindowSize({ w: viewport.w, h: viewport.h });
    };
    window.addEventListener(PAGE_ZOOM_CHANGED_EVENT, onPageZoomChange);
    return () => window.removeEventListener(PAGE_ZOOM_CHANGED_EVENT, onPageZoomChange);
  }, [isOpen, isMaximized, isMinimized]);

  const modalWidth = isMaximized ? windowSize.w : size.width;
  const modalHeight = isMaximized ? windowSize.h : size.height;
  const modalLeft = isMaximized ? 0 : position.x;
  const modalTop = isMaximized ? 0 : position.y;

  const handleHeaderMouseDown = useCallback((e: React.MouseEvent) => {
    if (isMaximized) return;
    const target = e.target as HTMLElement;
    if (target.closest('button') || target.tagName === 'BUTTON' || target.closest('input')) return;
    setIsDragging(true);
    setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
  }, [position, isMaximized]);

  useEffect(() => {
    if (!isDragging) return;
    const onMove = (e: MouseEvent) => {
      setPosition({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
    };
    const onUp = () => setIsDragging(false);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [isDragging, dragStart]);

  const handleResizeMouseDown = useCallback((e: React.MouseEvent, direction: 'nw' | 'ne' | 'sw' | 'se') => {
    if (isMinimized || isMaximized) return;
    e.stopPropagation();
    setResizeDirection(direction);
    setIsResizing(true);
    setResizeStart({ x: e.clientX, y: e.clientY, width: size.width, height: size.height, left: position.x, top: position.y });
  }, [size, position, isMinimized, isMaximized]);

  useEffect(() => {
    if (!isResizing || !resizeDirection) return;
    const onMove = (e: MouseEvent) => {
      const deltaX = e.clientX - resizeStart.x;
      const deltaY = e.clientY - resizeStart.y;
      let newWidth = resizeStart.width;
      let newHeight = resizeStart.height;
      let newX = resizeStart.left;
      let newY = resizeStart.top;

      switch (resizeDirection) {
        case 'nw':
          newWidth = Math.max(300, resizeStart.width - deltaX);
          newHeight = Math.max(350, resizeStart.height - deltaY);
          newX = resizeStart.left + (resizeStart.width - newWidth);
          newY = resizeStart.top + (resizeStart.height - newHeight);
          break;
        case 'ne':
          newWidth = Math.max(300, resizeStart.width + deltaX);
          newHeight = Math.max(350, resizeStart.height - deltaY);
          newY = resizeStart.top + (resizeStart.height - newHeight);
          break;
        case 'sw':
          newWidth = Math.max(300, resizeStart.width - deltaX);
          newHeight = Math.max(350, resizeStart.height + deltaY);
          newX = resizeStart.left + (resizeStart.width - newWidth);
          break;
        case 'se':
          newWidth = Math.max(300, resizeStart.width + deltaX);
          newHeight = Math.max(350, resizeStart.height + deltaY);
          break;
      }
      setSize({ width: newWidth, height: newHeight });
      setPosition({ x: newX, y: newY });
    };
    const onUp = () => {
      setIsResizing(false);
      setResizeDirection(null);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [isResizing, resizeDirection, resizeStart]);

  const handleStartNumberChange = (value: number) => {
    const safe = Math.max(1, Math.min(999, value));
    setStartNumber(Math.min(safe, endNumber));
  };
  const handleEndNumberChange = (value: number) => {
    const safe = Math.max(1, Math.min(999, value));
    setEndNumber(Math.max(startNumber, safe));
  };

  const handleDrawNumber = () => {
    if (availableNumbers.length === 0) return;
    const count = Math.min(continuousCount, availableNumbers.length);
    const drawnNumbers: number[] = [];
    const drawnSet = new Set(numberDrawnNumbers);
    for (let i = 0; i < count; i += 1) {
      const remaining = availableNumbers.filter((n) => !drawnSet.has(n));
      if (remaining.length === 0) break;
      const idx = Math.floor(Math.random() * remaining.length);
      drawnNumbers.push(remaining[idx]);
      drawnSet.add(remaining[idx]);
    }
    setNumberDrawnNumbers(drawnSet);
    if (count === 1) {
      setNumberResult(drawnNumbers[0] ?? null);
      setNumberResults([]);
    } else {
      setNumberResult(null);
      setNumberResults(drawnNumbers);
    }
  };

  const handleDrawProfile = () => {
    if (availableProfiles.length === 0) return;
    const count = Math.min(continuousCount, availableProfiles.length);
    const drawnMembers: ClassMember[] = [];
    const drawnSet = new Set(profileDrawnNumbers);
    for (let i = 0; i < count; i += 1) {
      const remaining = availableProfiles.filter((m) => {
        const n = toNumber(m.number);
        return !Number.isNaN(n) && !drawnSet.has(n);
      });
      if (remaining.length === 0) break;
      const idx = Math.floor(Math.random() * remaining.length);
      const selected = remaining[idx];
      drawnMembers.push(selected);
      const selectedNumber = toNumber(selected.number);
      if (!Number.isNaN(selectedNumber)) drawnSet.add(selectedNumber);
    }
    setProfileDrawnNumbers(drawnSet);
    if (count === 1) {
      setProfileResult(drawnMembers[0] ?? null);
      setProfileResults([]);
    } else {
      setProfileResult(null);
      setProfileResults(drawnMembers);
    }
  };

  const handleDrawSeat = () => {
    if (availableSeatMembers.length === 0) return;
    const count = Math.min(continuousCount, availableSeatMembers.length);
    const drawnMembers: ClassMember[] = [];
    const drawnSet = new Set(seatDrawnNumbers);
    for (let i = 0; i < count; i += 1) {
      const remaining = availableSeatMembers.filter((m) => {
        const n = toNumber(m.number);
        return !Number.isNaN(n) && !drawnSet.has(n);
      });
      if (remaining.length === 0) break;
      const idx = Math.floor(Math.random() * remaining.length);
      const selected = remaining[idx];
      drawnMembers.push(selected);
      const selectedNumber = toNumber(selected.number);
      if (!Number.isNaN(selectedNumber)) drawnSet.add(selectedNumber);
    }
    setSeatDrawnNumbers(drawnSet);
    if (count === 1) {
      setSeatResult(drawnMembers[0] ?? null);
      setSeatResults([]);
    } else {
      setSeatResult(null);
      setSeatResults(drawnMembers);
    }
  };

  const handleDivideTeams = () => {
    const matching = members.filter((m) => {
      const n = toNumber(m.number);
      return !Number.isNaN(n) && n >= startNumber && n <= endNumber;
    });
    if (matching.length === 0) return;
    const safeTeamCount = Math.max(2, Math.min(maxTeamCount, teamCount));
    const shuffled = [...matching].sort(() => Math.random() - 0.5);
    const teams: ClassMember[][] = Array.from({ length: safeTeamCount }, () => []);
    shuffled.forEach((member, index) => {
      teams[index % safeTeamCount].push(member);
    });
    setTeamResult(teams);
    setShowTeamCountModal(false);
  };

  const handleResetByTab = () => {
    if (selectedTab === 'number') {
      setNumberDrawnNumbers(new Set());
      setNumberResult(null);
      setNumberResults([]);
      return;
    }
    if (selectedTab === 'profile') {
      setProfileDrawnNumbers(new Set());
      setProfileResult(null);
      setProfileResults([]);
      return;
    }
    if (selectedTab === 'seat') {
      setSeatDrawnNumbers(new Set());
      setSeatResult(null);
      setSeatResults([]);
      return;
    }
    setTeamResult(null);
  };

  const getSeatCardVisual = (member: ClassMember) => {
    if (currentSeatHighlightIds.has(member.id)) return 'highlight' as const;
    if (!hasAnySeatDraw) return 'idle' as const;
    const n = toNumber(member.number);
    if (!Number.isNaN(n) && seatDrawnNumbers.has(n)) return 'drawn' as const;
    return 'faded' as const;
  };

  if (!isOpen || isHidden) return null;

  return createPortal(
    <DiceModalOverlay
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) {
          // 최소화 상태에서는 배경 클릭으로 닫지 않고 숨김 처리(상태 유지)
          if (isMinimized) {
            // 최소화 상태: 닫히지 않도록 아무 동작도 하지 않음
            return;
          } else {
            onClose();
          }
        }
      }}
      onTouchStart={(e) => {
        if (e.target === e.currentTarget) {
          // 최소화 상태에서는 배경 클릭으로 닫지 않고 숨김 처리(상태 유지)
          if (isMinimized) {
            // 최소화 상태: 닫히지 않도록 아무 동작도 하지 않음
            return;
          } else {
            onClose();
          }
        }
      }}
    >
      <DiceModalContent
        $isMinimized={isMinimized}
        $isMaximized={isMaximized}
        $left={modalLeft}
        $top={modalTop}
        $width={modalWidth}
        $height={modalHeight}
      >
        <DiceModalHeader onMouseDown={handleHeaderMouseDown}>
          <DiceModalTitle>뽑기</DiceModalTitle>
          <DiceModalControls>
            <DiceModalButton
              onClick={() => {
                setIsMinimized(true);
                setIsMaximized(false);
              }}
              disabled={isMinimized}
              title="최소화"
            >
              −
            </DiceModalButton>
            <DiceModalButton
              onClick={() => {
                if (isMinimized) setIsMinimized(false);
                else if (isMaximized) setIsMaximized(false);
                else setIsMaximized(true);
              }}
              title={isMinimized ? '중간 모드' : isMaximized ? '중간 모드' : '최대 확장'}
            >
              {isMinimized || isMaximized ? '◫' : '□'}
            </DiceModalButton>
            <DiceModalButton onClick={onClose} title="닫기">
              ×
            </DiceModalButton>
          </DiceModalControls>
        </DiceModalHeader>

        <DiceModalBody $isMinimized={isMinimized}>
          {isMinimized ? (
            <MinimizedLabel>뽑기</MinimizedLabel>
          ) : (
            <DiceFormSection>
              <NumberInputRow>
                <NumberInputGroup>
                  <NumberInputWrapper>
                    <NumberInputLabel>시작</NumberInputLabel>
                    <NumberInput type="number" value={startNumber} onChange={(e) => handleStartNumberChange(parseInt(e.target.value, 10) || 1)} />
                    <NumberInputContainer>
                      <IconButton onClick={() => setStartNumber(defaultStart)} title="초기화">
                        <ResetIcon src="/reset.svg" alt="초기화" />
                      </IconButton>
                      <IncrementButton onClick={() => setStartNumber((v) => Math.max(1, v - 1))} disabled={startNumber <= 1}>-</IncrementButton>
                      <IncrementButton onClick={() => setStartNumber((v) => Math.min(endNumber, v + 1))} disabled={startNumber >= endNumber}>+</IncrementButton>
                    </NumberInputContainer>
                  </NumberInputWrapper>
                </NumberInputGroup>
                <NumberInputGroup>
                  <NumberInputWrapper>
                    <NumberInputLabel>끝</NumberInputLabel>
                    <NumberInput type="number" value={endNumber} onChange={(e) => handleEndNumberChange(parseInt(e.target.value, 10) || 30)} />
                    <NumberInputContainer>
                      <IconButton onClick={() => setEndNumber(defaultEnd)} title="초기화">
                        <ResetIcon src="/reset.svg" alt="초기화" />
                      </IconButton>
                      <IncrementButton onClick={() => setEndNumber((v) => Math.max(startNumber, v - 1))} disabled={endNumber <= startNumber}>-</IncrementButton>
                      <IncrementButton onClick={() => setEndNumber((v) => Math.min(999, v + 1))} disabled={endNumber >= 999}>+</IncrementButton>
                    </NumberInputContainer>
                  </NumberInputWrapper>
                </NumberInputGroup>
              </NumberInputRow>

              <DiceActionButtonRow>
                <ActionButton $active={selectedTab === 'number'} onClick={() => setSelectedTab('number')}>번호 뽑기</ActionButton>
                <ActionButton $active={selectedTab === 'profile'} onClick={() => setSelectedTab('profile')}>프로필 뽑기</ActionButton>
                <ActionButton $active={selectedTab === 'seat'} onClick={() => setSelectedTab('seat')}>자리 뽑기</ActionButton>
                <ActionButton $active={selectedTab === 'team'} onClick={() => setSelectedTab('team')}>팀 나누기</ActionButton>
              </DiceActionButtonRow>

              {selectedTab && (
                <DiceTabContent>
                  <ResultSection>
                    <ResultHeader>
                      <ResultHeaderLeft>
                        {(selectedTab === 'number' || selectedTab === 'profile' || selectedTab === 'seat') && (
                          <ContinuousInputWrapper>
                            <ContinuousTopRow>
                              <ContinuousLabel>연속</ContinuousLabel>
                              <ContinuousInput
                                type="number"
                                value={continuousCount}
                                onChange={(e) => setContinuousCount(Math.max(1, Math.min(100, parseInt(e.target.value, 10) || 1)))}
                              />
                            </ContinuousTopRow>
                            <ContinuousBottomRow>
                              <IncrementButton
                                onClick={() => setContinuousCount((v) => Math.max(1, v - 1))}
                                disabled={continuousCount <= 1}
                              >
                                -
                              </IncrementButton>
                              <IncrementButton
                                onClick={() => setContinuousCount((v) => Math.min(100, v + 1))}
                                disabled={continuousCount >= 100}
                              >
                                +
                              </IncrementButton>
                            </ContinuousBottomRow>
                          </ContinuousInputWrapper>
                        )}
                      </ResultHeaderLeft>

                      <ResultHeaderCenter>
                        <ResultDiceButton
                          onClick={() => {
                            if (selectedTab === 'number') handleDrawNumber();
                            else if (selectedTab === 'profile') handleDrawProfile();
                            else if (selectedTab === 'seat') handleDrawSeat();
                            else setShowTeamCountModal(true);
                          }}
                        >
                          <img src="/Dice.svg" alt="주사위" />
                        </ResultDiceButton>
                      </ResultHeaderCenter>

                      <ResultHeaderRight>
                        {selectedTab === 'number' && (
                          <RemainingCount>
                            남은수&nbsp;&nbsp;:&nbsp;{pad2(numberRemainingCount)}
                          </RemainingCount>
                        )}
                        {selectedTab === 'profile' && (
                          <RemainingCount>
                            남은수&nbsp;&nbsp;:&nbsp;{pad2(profileRemainingCount)}
                          </RemainingCount>
                        )}
                        {selectedTab === 'seat' && (
                          <RemainingCount>
                            남은수&nbsp;&nbsp;:&nbsp;{pad2(seatRemainingCount)}
                          </RemainingCount>
                        )}
                        {selectedTab === 'team' ? (
                          <ResetResultButton onClick={handleResetByTab}>초기화</ResetResultButton>
                        ) : (
                          <ResetResultButton onClick={handleResetByTab}>초기화</ResetResultButton>
                        )}
                      </ResultHeaderRight>
                    </ResultHeader>

                    <ResultContent>
                      {selectedTab === 'number' && (
                        <>
                          {numberResult !== null && numberResults.length === 0 && (
                            <NumberResultsGrid>
                              <NumberResultItem>{numberResult}</NumberResultItem>
                            </NumberResultsGrid>
                          )}
                          {numberResults.length > 0 && (
                            <NumberResultsGrid>
                              {numberResults.map((num, idx) => (
                                <NumberResultItem key={`${num}-${idx}`}>{num}</NumberResultItem>
                              ))}
                            </NumberResultsGrid>
                          )}
                        </>
                      )}

                      {selectedTab === 'profile' && (
                        <>
                          {profileResult && profileResults.length === 0 && (
                            <ProfileResultsGrid>
                              <ProfileResultCard>
                                <ProfileResultCardImageWrapper>
                                  <CachedProfileImage imageId={resolveMemberProfileSource(profileResult, memberProfileDisplayMode)} alt={profileResult.name || '프로필'} fallbackImageId="basic-1" />
                                </ProfileResultCardImageWrapper>
                                <ProfileResultCardInfo>
                                  {profileResult.number}번{profileResult.name ? ` ${profileResult.name}` : ''}
                                </ProfileResultCardInfo>
                              </ProfileResultCard>
                            </ProfileResultsGrid>
                          )}
                          {profileResults.length > 0 && (
                            <ProfileResultsGrid>
                              {profileResults.map((member) => (
                                <ProfileResultCard key={member.id}>
                                  <ProfileResultCardImageWrapper>
                                    <CachedProfileImage imageId={resolveMemberProfileSource(member, memberProfileDisplayMode)} alt={member.name || '프로필'} fallbackImageId="basic-0" />
                                  </ProfileResultCardImageWrapper>
                                  <ProfileResultCardInfo>
                                    {member.number}번{member.name ? ` ${member.name}` : ''}
                                  </ProfileResultCardInfo>
                                </ProfileResultCard>
                              ))}
                            </ProfileResultsGrid>
                          )}
                          <ProfileScrollBottomSpacer />
                        </>
                      )}

                      {selectedTab === 'seat' && (
                        <SeatTabWrap>
                          {!seatMonthKey ? (
                            <SeatTabHint>저장된 자리 배정이 없습니다.</SeatTabHint>
                          ) : seatAssignedItems.length === 0 ? (
                            <SeatTabHint>시작~끝 번호 범위에 자리가 배정된 학생이 없습니다.</SeatTabHint>
                          ) : (
                            <>
                              <DiceSeatTableOuter>
                                <DiceSeatGridScroll>
                                  <DiceSeatGrid
                                    $gridTemplateColumns={diceSeatLayout.colTemplate}
                                    $gridTemplateRows={diceSeatLayout.rowTemplate}
                                  >
                                    {diceSeatLayout.assigned.map(({ member, seatRow, seatCol }) => {
                                      const gridRow = seatRow - diceSeatLayout.minRow + 1;
                                      const gridCol = seatCol - diceSeatLayout.minCol + 1;
                                      const visual = getSeatCardVisual(member);
                                      const numLabel = String(toNumber(member.number) || member.number || '0').padStart(2, '0');
                                      const nameText = (member.name || '').trim();
                                      const nameLength = Array.from(nameText).length;
                                      // 이름이 길수록 폰트를 더 줄여 ... 없이 블럭 안에 맞춤
                                      const nameFitScale = seatNarrowLayout
                                        ? 1
                                        : Math.max(0.52, Math.min(1, 8 / Math.max(1, nameLength)));
                                      return (
                                        <DiceSeatCell
                                          key={member.id}
                                          style={{
                                            gridColumn: `${gridCol} / span 2`,
                                            gridRow: `${gridRow} / span 2`,
                                          }}
                                        >
                                          <DiceSeatStudentCard $visual={visual} $compact={seatNarrowLayout} $seatScale={seatScale}>
                                            {!seatNarrowLayout && (
                                              <DiceSeatProfileWrap $seatScale={seatScale}>
                                                <DiceSeatProfileImg
                                                  imageId={resolveMemberProfileSource(member, memberProfileDisplayMode)}
                                                  alt={`${member.name || '학생'} 프로필`}
                                                  fallbackImageId="basic-0"
                                                />
                                              </DiceSeatProfileWrap>
                                            )}
                                            <DiceSeatTextCol $compact={seatNarrowLayout}>
                                              <DiceSeatInfoRow $compact={seatNarrowLayout} $seatScale={seatScale}>
                                                {!seatNarrowLayout && (
                                                  <DiceSeatNumberLabel $seatScale={seatScale}>{numLabel}</DiceSeatNumberLabel>
                                                )}
                                                <DiceSeatNameLabel $seatScale={seatScale} $compact={seatNarrowLayout} $nameFitScale={nameFitScale}>
                                                  {nameText}
                                                </DiceSeatNameLabel>
                                              </DiceSeatInfoRow>
                                            </DiceSeatTextCol>
                                          </DiceSeatStudentCard>
                                        </DiceSeatCell>
                                      );
                                    })}
                                  </DiceSeatGrid>
                                </DiceSeatGridScroll>
                              </DiceSeatTableOuter>
                              <DiceSeatRotateButtonContainer>
                                <DiceSeatRotateButton
                                  onClick={() => setIsSeatRotated((p) => !p)}
                                  title="180도 회전"
                                >
                                  <DiceSeatRotateIconWrapper>
                                    <DiceSeatRotateArrowIcon>
                                      <svg
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        aria-hidden
                                      >
                                        <path d="M12 4v16M12 4l-4 4M12 4l4 4M12 20l-4-4M12 20l4-4" />
                                      </svg>
                                    </DiceSeatRotateArrowIcon>
                                    <DiceSeatRotateNumberIcon>180</DiceSeatRotateNumberIcon>
                                  </DiceSeatRotateIconWrapper>
                                </DiceSeatRotateButton>
                              </DiceSeatRotateButtonContainer>
                            </>
                          )}
                        </SeatTabWrap>
                      )}

                      {selectedTab === 'team' && teamResult !== null && (
                        <TeamResult>
                          {teamResult.map((team, teamIndex) => (
                            <TeamBlock key={teamIndex}>
                              <TeamTitle>팀 {teamIndex + 1}</TeamTitle>
                              <TeamMembersGrid>
                                {team.map((member) => (
                                  <TeamMemberCard key={member.id}>
                                    <TeamMemberImage>
                                      <CachedProfileImage imageId={resolveMemberProfileSource(member, memberProfileDisplayMode)} alt={member.name || '프로필'} fallbackImageId="basic-0" />
                                    </TeamMemberImage>
                                    <TeamMemberInfo>
                                      {member.number}번{member.name ? ` ${member.name}` : ''}
                                    </TeamMemberInfo>
                                  </TeamMemberCard>
                                ))}
                              </TeamMembersGrid>
                            </TeamBlock>
                          ))}
                        </TeamResult>
                      )}
                    </ResultContent>
                  </ResultSection>
                </DiceTabContent>
              )}
            </DiceFormSection>
          )}
        </DiceModalBody>

        {showTeamCountModal && (
          <TeamCountModalOverlay onClick={() => setShowTeamCountModal(false)}>
            <TeamCountModalContent onClick={(e) => e.stopPropagation()}>
              <TeamCountModalHeader>
                <h3>팀 수 설정</h3>
                <CloseModalButton onClick={() => setShowTeamCountModal(false)}>×</CloseModalButton>
              </TeamCountModalHeader>
              <TeamCountInputWrapper>
                <TeamCountLabel>팀 수 (최대 {maxTeamCount}팀)</TeamCountLabel>
                <TeamCountInput
                  type="number"
                  value={teamCount}
                  min={2}
                  max={maxTeamCount}
                  onChange={(e) => {
                    const next = parseInt(e.target.value, 10);
                    if (!Number.isNaN(next)) {
                      setTeamCount(Math.max(2, Math.min(maxTeamCount, next)));
                    }
                  }}
                />
                <TeamCountButtonContainer>
                  <TeamCountIncrementButton onClick={() => setTeamCount((v) => Math.max(2, v - 1))} disabled={teamCount <= 2}>-</TeamCountIncrementButton>
                  <TeamCountIconButton onClick={() => setTeamCount(2)} title="초기화">
                    <img src="/reset.svg" alt="초기화" />
                  </TeamCountIconButton>
                  <TeamCountIncrementButton onClick={() => setTeamCount((v) => Math.min(maxTeamCount, v + 1))} disabled={teamCount >= maxTeamCount}>+</TeamCountIncrementButton>
                </TeamCountButtonContainer>
              </TeamCountInputWrapper>
              <TeamCountButtonRow>
                <TeamCountButton $variant="secondary" onClick={() => setShowTeamCountModal(false)}>취소</TeamCountButton>
                <TeamCountButton $variant="primary" onClick={handleDivideTeams}>확인</TeamCountButton>
              </TeamCountButtonRow>
            </TeamCountModalContent>
          </TeamCountModalOverlay>
        )}

        {!isMinimized && !isMaximized && (
          <>
            <ResizeHandle style={{ top: 0, left: 0, cursor: 'nwse-resize' }} onMouseDown={(e) => handleResizeMouseDown(e, 'nw')} />
            <ResizeHandle style={{ top: 0, right: 0, cursor: 'nesw-resize' }} onMouseDown={(e) => handleResizeMouseDown(e, 'ne')} />
            <ResizeHandle style={{ bottom: 0, left: 0, cursor: 'nesw-resize' }} onMouseDown={(e) => handleResizeMouseDown(e, 'sw')} />
            <ResizeHandle style={{ bottom: 0, right: 0, cursor: 'nwse-resize' }} onMouseDown={(e) => handleResizeMouseDown(e, 'se')} />
          </>
        )}
      </DiceModalContent>
    </DiceModalOverlay>,
    document.body
  );
};

const DiceModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 10000;
  pointer-events: auto;
`;

const DiceModalContent = styled.div<{ $isMinimized: boolean; $isMaximized: boolean; $left: number; $top: number; $width: number; $height: number }>`
  position: fixed;
  left: ${(p) => p.$left}px;
  top: ${(p) => p.$top}px;
  width: ${(p) => (p.$isMinimized ? '170px' : `${p.$width}px`)};
  height: ${(p) => (p.$isMinimized ? '82px' : `${p.$height}px`)};
  min-width: ${(p) => (p.$isMinimized ? '170px' : '300px')};
  min-height: ${(p) => (p.$isMinimized ? '82px' : '350px')};
  background: var(--color-accent-strong);
  border: ${(p) => (p.$isMaximized ? 'none' : '2px solid rgba(255, 255, 255, 0.45)')};
  border-radius: ${(p) => (p.$isMaximized ? '0' : '12px')};
  box-shadow: ${(p) => (p.$isMaximized ? 'none' : '0 8px 24px rgba(0, 0, 0, 0.3)')};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  pointer-events: auto;
`;

const DiceModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 36px;
  height: 36px;
  padding: 6px 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.45);
  background: rgba(255, 255, 255, 0.2);
  color: white;
  cursor: move;
  user-select: none;
  flex-shrink: 0;
  :root:not([data-theme='light']) & {
    background: #404040;
  }
`;

const DiceModalTitle = styled.div`
  font-size: 14px;
  font-weight: 600;

  @media (max-width: 500px) {
    font-size: 12px;
  }
`;

const DiceModalControls = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const DiceModalButton = styled.button.attrs({ type: 'button', className: 'touch-compact' })`
  width: 24px;
  height: 24px;
  border: none;
  background: transparent;
  color: white;
  cursor: pointer;
  border-radius: 4px;
  font-size: 16px;
  line-height: 1;
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media (max-width: 500px) {
    width: 20px;
    height: 20px;
    font-size: 14px;
  }
`;

const DiceModalBody = styled.div<{ $isMinimized: boolean }>`
  flex: 1;
  overflow: hidden;
  background: transparent;
  padding: ${(p) => (p.$isMinimized ? '8px 12px' : '0')};
  :root:not([data-theme='light']) & {
    background: #141414;
  }
`;

const MinimizedLabel = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 16px;
  font-weight: 700;

  @media (max-width: 500px) {
    font-size: 14px;
  }
`;

const DiceFormSection = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 8px 20px 20px 20px;
  gap: 16px;
  overflow-y: auto;
  overflow-x: hidden;
  user-select: none;

  @media (max-width: 500px) {
    padding: 8px 12px 16px 12px;
    gap: 12px;
  }
`;

const NumberInputRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  flex-wrap: wrap;
`;
const NumberInputGroup = styled.div`
  display: flex;
  align-items: center;
`;
const NumberInputWrapper = styled.div`
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 8px;
  min-width: 220px;
  border: 1.5px dashed rgba(255, 255, 255, 0.5);
  border-radius: 8px;
  padding: 6px 10px;
  min-height: 36px;
  height: 36px;

  @media (max-width: 500px) {
    min-width: 0;
    padding: 4px 8px;
    min-height: 32px;
    height: 32px;
    gap: 6px;
  }
`;
const NumberInputLabel = styled.span`
  font-size: 14px;
  color: white;
  font-weight: 500;
`;
const NumberInput = styled.input`
  width: 100%;
  max-width: 80px;
  min-width: 50px;
  border: none;
  background: transparent;
  color: white;
  font-size: 20px;
  font-weight: 600;
  text-align: center;
  outline: none;
  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  @media (max-width: 500px) {
    max-width: 62px;
    min-width: 40px;
    font-size: 18px;
  }
`;
const NumberInputContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;
const IconButton = styled.button.attrs({ type: 'button', className: 'touch-compact' })`
  width: 24px;
  height: 24px;
  border: 1px solid rgba(255, 255, 255, 0.45);
  background: transparent;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  @media (max-width: 500px) {
    width: 20px;
    height: 20px;
  }
`;
const ResetIcon = styled.img`
  width: 14px;
  height: 14px;
  filter: brightness(0) invert(1);
`;
const IncrementButton = styled.button.attrs({ type: 'button', className: 'touch-compact' })`
  width: 24px;
  height: 24px;
  border: 1px solid rgba(255, 255, 255, 0.45);
  background: transparent;
  color: white;
  border-radius: 4px;
  cursor: pointer;
  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  @media (max-width: 500px) {
    width: 20px;
    height: 20px;
  }
`;

const DiceActionButtonRow = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 12px;

  @media (max-width: 500px) {
    gap: 8px;
  }
`;
const ActionButton = styled.button.attrs({ type: 'button', className: 'touch-compact' })<{ $active: boolean }>`
  height: 36px;
  min-width: 112px;
  padding: 0 14px;
  border-radius: 8px;
  border: 1px solid ${(p) => (p.$active ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.45)')};
  background: ${(p) => (p.$active ? 'rgba(255,255,255,0.2)' : 'transparent')};
  color: white;
  font-weight: 700;
  cursor: pointer;

  @media (max-width: 500px) {
    height: 32px;
    min-width: 0;
    padding: 0 10px;
    flex: 1;
  }
`;

const DiceTabContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;
`;
const ResultSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-top: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.35);
  min-height: 200px;
`;
const ResultHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  min-height: 56px;

  @media (max-width: 500px) {
    min-height: 48px;
  }
`;
const ResultHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;
const ResultHeaderCenter = styled.div`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
`;
const ResultHeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  @media (max-width: 500px) {
    flex-direction: column;
    align-items: flex-end;
    gap: 6px;
  }
`;
const ContinuousInputWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  border: 1.5px dashed rgba(255, 255, 255, 0.5);
  border-radius: 8px;
  padding: 6px 10px;
  min-height: 36px;

  @media (max-width: 500px) {
    flex-direction: column;
    align-items: center;
    gap: 6px;
    padding: 4px 8px;
    min-height: 32px;
  }
`;

const ContinuousTopRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ContinuousBottomRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ContinuousParen = styled.span`
  font-size: 14px;
  color: white;
  font-weight: 500;

  @media (max-width: 500px) {
    font-size: 12px;
  }
`;
const ContinuousLabel = styled.span`
  font-size: 14px;
  color: white;
  font-weight: 500;

  @media (max-width: 500px) {
    font-size: 12px;
  }
`;
const ContinuousInput = styled.input`
  width: 28px;
  border: none;
  background: transparent;
  color: white;
  font-size: 16px;
  font-weight: 600;
  text-align: center;
  outline: none;

  @media (max-width: 500px) {
    width: 22px;
    font-size: 14px;
  }
`;
const RemainingCount = styled.div`
  color: rgba(255, 255, 255, 0.85);
  font-size: 14px;

  @media (max-width: 500px) {
    font-size: 12px;
  }
`;
const ResetResultButton = styled.button.attrs({ type: 'button', className: 'touch-compact' })`
  padding: 6px 12px;
  border: 1px solid rgba(255, 255, 255, 0.45);
  border-radius: 6px;
  background: transparent;
  color: white;
  font-size: 13px;
  cursor: pointer;

  @media (max-width: 500px) {
    padding: 5px 10px;
    font-size: 12px;
  }
`;
const ResultDiceButton = styled.button.attrs({ type: 'button', className: 'touch-compact' })`
  width: 48px;
  height: 48px;
  border: 2px solid rgba(255, 255, 255, 0.45);
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  img {
    width: 28px;
    height: 28px;
    filter: brightness(0) invert(1);
  }

  @media (max-width: 500px) {
    width: 40px;
    height: 40px;
    img {
      width: 24px;
      height: 24px;
    }
  }
`;

const ResultContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  width: 100%;
`;
const NumberResultsGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  padding: 20px;
  width: 100%;
  justify-content: center;

  @media (max-width: 500px) {
    gap: 12px;
    padding: 14px;
  }
`;
const NumberResultItem = styled.div`
  font-size: clamp(42px, 6vw, 96px);
  font-weight: bold;
  color: white;
  text-align: center;
  border: 2px solid rgba(255, 255, 255, 0.45);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.05);
  width: min(240px, 22vw);
  height: min(240px, 22vw);
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 500px) {
    font-size: clamp(34px, 9vw, 64px);
    border-radius: 10px;
    width: min(160px, 36vw);
    height: min(160px, 36vw);
  }
`;

const ProfileResultsGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: clamp(16px, 2vw, 32px);
  padding: clamp(16px, 2vw, 24px);
  width: 100%;
  justify-content: center;

  @media (max-width: 500px) {
    gap: 12px;
    padding: 14px;
  }
`;
const ProfileResultCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  width: min(280px, 20vw);

  @media (max-width: 500px) {
    gap: 8px;
    width: min(200px, 36vw);
  }
`;
const ProfileResultCardImageWrapper = styled.div`
  width: 100%;
  aspect-ratio: 1 / 1;
  border-radius: 16px;
  overflow: hidden;
  border: 5px solid #000000;

  @media (max-width: 500px) {
    border-radius: 12px;
    border-width: 3px;
  }
`;
const ProfileResultCardInfo = styled.div`
  text-align: center;
  color: white;
  font-size: clamp(24px, 2.4vw, 40px);
  font-weight: 800;
  width: 100%;
  line-height: 1.25;
  word-break: keep-all;
  overflow-wrap: anywhere;

  @media (max-width: 500px) {
    font-size: clamp(18px, 4.4vw, 28px);
  }
`;
const ProfileScrollBottomSpacer = styled.div`
  width: 100%;
  height: 300px;
  flex-shrink: 0;
`;

const SeatTabWrap = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 10px;
`;

const SeatTabHint = styled.div`
  width: 100%;
  text-align: center;
  color: rgba(255, 255, 255, 0.92);
  font-size: 14px;
  font-weight: 600;
  padding: 16px 12px;
  line-height: 1.5;
`;

/* 개인공지(자리 모드) SeatModeScrollArea 와 유사 — 배경 투명, 가로 스크롤 */
const DiceSeatTableOuter = styled.div`
  width: 100%;
  min-width: 0;
  background: transparent;
  border-radius: 8px;
  overflow: visible;
`;

const DiceSeatGridScroll = styled.div`
  width: 100%;
  min-width: 0;
  background: transparent;
  border-radius: 8px;
  padding: 16px 2px;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;

  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.25) transparent;

  &::-webkit-scrollbar {
    height: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: rgba(255, 255, 255, 0.22);
    border-radius: 999px;
  }

  &:hover::-webkit-scrollbar-thumb {
    background-color: var(--color-block-border);
  }
`;

/* IndividualNoticeSeatGrid 와 동일: gap 2px, padding 4px */
const DiceSeatGrid = styled.div<{ $gridTemplateColumns: string; $gridTemplateRows: string }>`
  display: grid;
  grid-template-columns: ${(p) => p.$gridTemplateColumns};
  grid-template-rows: ${(p) => p.$gridTemplateRows};
  gap: 2px;
  padding: 4px;
  width: 100%;
  min-width: max-content;
  align-content: start;
  justify-content: center;
`;

/* 그리드 셀(span 2행) 높이에 맞춤 — 고정 min-height 제거로 인접 블럭과 겹침 방지 */
const DiceSeatCell = styled.div`
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
  display: flex;
  align-items: stretch;
  justify-content: center;
  box-sizing: border-box;
`;

/* 개인공지 IndividualNoticeStudentCard 기반 + 뽑기 상태(highlight/drawn/faded) */
const DiceSeatStudentCard = styled.div<{
  $visual: 'idle' | 'highlight' | 'drawn' | 'faded';
  $compact: boolean;
  $seatScale: number;
}>`
  display: grid;
  grid-template-columns: ${(p) => (p.$compact ? '1fr' : 'auto 1fr')};
  align-items: ${(p) => (p.$compact ? 'center' : 'stretch')};
  justify-items: ${(p) => (p.$compact ? 'center' : 'stretch')};
  gap: ${(p) => `${Math.max(2, Math.round(8 * p.$seatScale))}px`};
  width: 100%;
  height: 100%;
  max-height: 100%;
  min-width: 0;
  min-height: 0;
  box-sizing: border-box;
  border-radius: ${(p) => `${Math.max(4, Math.round(8 * p.$seatScale))}px`};
  padding: ${(p) =>
    `${Math.max(2, Math.round(4 * p.$seatScale))}px ${Math.max(2, Math.round(6 * p.$seatScale))}px`};
  overflow: hidden;
  cursor: default;
  transition:
    opacity 0.2s ease,
    filter 0.2s ease,
    border-color 0.2s ease,
    box-shadow 0.2s ease,
    transform 0.2s ease;

  background: rgba(255, 255, 255, 0.1);
  :root:not([data-theme='light']) & {
    background: transparent;
  }

  border: 2px solid var(--color-border);
  :root[data-theme='light'] & {
    border-color: var(--color-block-border);
  }
  :root:not([data-theme='light']) & {
    border-color: var(--color-block-border);
  }

  &:hover {
    /* 세로 여백이 좁아 translate 시 아래 행과 겹칠 수 있어 미세만 */
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
  }

  ${(p) => {
    switch (p.$visual) {
      case 'highlight':
        return `
          opacity: 1 !important;
          filter: none !important;
          border-color: rgba(255, 255, 255, 0.95) !important;
          box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.85), 0 8px 22px rgba(0, 0, 0, 0.2);
          position: relative;
          z-index: 2;
        `;
      case 'drawn':
        return `
          opacity: 0.28;
          filter: grayscale(1) brightness(0.8);
          border-color: rgba(255, 255, 255, 0.35);
        `;
      case 'faded':
        return `
          opacity: 0.6;
          filter: none;
        `;
      default:
        return `
          opacity: 1;
          filter: none;
        `;
    }
  }}
`;

/* 블럭(셀) 안쪽 세로 높이에 맞춘 정사각형 — 가로는 세로에 맞춰 자동 */
const DiceSeatProfileWrap = styled.div<{ $seatScale: number }>`
  align-self: stretch;
  justify-self: start;
  height: 100%;
  max-height: 100%;
  min-height: 0;
  width: auto;
  aspect-ratio: 1;
  max-width: 100%;
  min-width: ${(p) => `${Math.max(24, Math.round(40 * p.$seatScale))}px`};
  border-radius: ${(p) => `${Math.max(4, Math.round(6 * p.$seatScale))}px`};
  overflow: hidden;
  border: 2px solid #000000;
  background: transparent;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.02);
  }
`;

const DiceSeatProfileImg = styled(CachedProfileImage)`
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
  max-width: 100%;
`;

const DiceSeatTextCol = styled.div<{ $compact: boolean }>`
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: ${(p) => (p.$compact ? 'center' : 'stretch')};
  align-self: stretch;
  width: ${(p) => (p.$compact ? '100%' : 'auto')};
  overflow: hidden;
`;

const DiceSeatInfoRow = styled.div<{ $compact: boolean; $seatScale: number }>`
  display: flex;
  flex-direction: column;
  align-items: ${(p) => (p.$compact ? 'center' : 'flex-start')};
  justify-content: center;
  gap: ${(p) => (p.$compact ? 0 : `${Math.max(2, Math.round(4 * p.$seatScale))}px`)};
  min-width: 0;
  width: ${(p) => (p.$compact ? '100%' : 'auto')};
  text-align: ${(p) => (p.$compact ? 'center' : 'left')};
`;

const DiceSeatNumberLabel = styled.span<{ $seatScale: number }>`
  font-size: ${(p) => `${Math.max(9, Math.round(17 * p.$seatScale))}px`};
  line-height: 1.15;
  color: var(--color-text-gray);
  font-weight: 800;
  flex-shrink: 0;
  -webkit-text-size-adjust: 100%;
  text-size-adjust: 100%;

  :root[data-theme='light'] & {
    color: #ddd;
  }
`;

const DiceSeatNameLabel = styled.span<{ $seatScale: number; $compact: boolean; $nameFitScale: number }>`
  font-size: ${(p) => `${Math.max(10, Math.round(30 * p.$seatScale * p.$nameFitScale))}px`};
  line-height: ${(p) => (p.$compact ? 1.25 : 1.2)};
  font-weight: 700;
  color: var(--color-text-primary);
  max-width: 100%;
  -webkit-text-size-adjust: 100%;
  text-size-adjust: 100%;

  ${(p) =>
    p.$compact
      ? `
    white-space: normal;
    overflow: hidden;
    text-align: center;
    word-break: break-word;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
  `
      : `
    white-space: nowrap;
    overflow: hidden;
    text-overflow: clip;
    display: block;
  `}

  :root[data-theme='light'] & {
    color: #ffffff;
  }
`;

const TeamResult = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  align-items: center;
`;
const TeamBlock = styled.div`
  border: 2px solid rgba(255, 255, 255, 0.45);
  border-radius: 8px;
  padding: 16px;
  background: rgba(255, 255, 255, 0.05);
  max-width: 100%;
`;
const TeamTitle = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: white;
  margin-bottom: 12px;
  text-align: center;
`;
const TeamMembersGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: center;
`;
const TeamMemberCard = styled.div`
  width: min(150px, 13vw);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;
const TeamMemberImage = styled.div`
  width: 100%;
  aspect-ratio: 1/1;
  border-radius: 8px;
  overflow: hidden;
  border: 3px solid #000;
`;
const TeamMemberInfo = styled.div`
  color: white;
  font-size: 14px;
  font-weight: 700;
  text-align: center;
  line-height: 1.3;
  word-break: keep-all;
  overflow-wrap: anywhere;
  max-width: 100%;
`;

const TeamCountModalOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 20;
`;
const TeamCountModalContent = styled.div`
  width: min(420px, 88vw);
  border-radius: 12px;
  border: var(--modal-shell-border);
  box-shadow: var(--modal-shadow-large);
  background: var(--color-surface);
  color: var(--color-text-primary);
  padding: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;
const TeamCountModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  h3 {
    margin: 0;
    font-size: 18px;
    text-align: center;
    flex: 1;
  }
`;
const CloseModalButton = styled.button.attrs({ type: 'button', className: 'touch-compact' })`
  border: none;
  background: transparent;
  font-size: 22px;
  cursor: pointer;
  color: var(--color-text-primary);
`;
const TeamCountInputWrapper = styled.div`
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  align-items: center;
`;
const TeamCountLabel = styled.label`
  font-size: 14px;
  font-weight: 600;
  text-align: center;
`;
const TeamCountInput = styled.input`
  height: 38px;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  padding: 0 10px;
  width: 100%;
  max-width: 220px;
  text-align: center;
`;
const TeamCountButtonContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: center;
`;
const TeamCountIncrementButton = styled.button.attrs({ type: 'button', className: 'touch-compact' })`
  width: 34px;
  height: 34px;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  background: var(--color-surface);
  cursor: pointer;
`;
const TeamCountIconButton = styled.button.attrs({ type: 'button', className: 'touch-compact' })`
  width: 34px;
  height: 34px;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  background: var(--color-surface);
  cursor: pointer;
  img {
    width: 14px;
    height: 14px;
  }
`;
const TeamCountButtonRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 16px;
  width: 100%;
`;
const TeamCountButton = styled.button.attrs({ type: 'button', className: 'touch-compact' })<{
  $variant?: 'primary' | 'secondary';
}>`
  flex: 1;
  min-width: 0;
  height: 36px;
  border-radius: 8px;
  border: ${(p) => (p.$variant === 'primary' ? 'none' : '1px solid var(--color-border)')};
  background: ${(p) => (p.$variant === 'primary' ? 'var(--color-accent-strong)' : 'var(--color-surface)')};
  color: ${(p) => (p.$variant === 'primary' ? 'white' : 'var(--color-text-primary)')};
  cursor: pointer;
`;

const ResizeHandle = styled.div`
  position: absolute;
  width: 20px;
  height: 20px;
  background: transparent;
  z-index: 30;
`;

export default DiceModal;
